import { cn } from '@/lib/utils';
import { ElementRef, memo, useCallback, useEffect, useRef, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';

type TitleProp = {
  autofocus?: boolean;
  title: string;
  onChange: (newTitle: string) => void;
  onComplete: () => void;
  placeholder?: string;
  bold?: boolean;
};

const CollectionTitle: React.FC<TitleProp> = ({
  title,
  placeholder,
  autofocus = false,
  bold = true,
  onChange,
  onComplete,
}) => {
  const inputRef = useRef<ElementRef<'textarea'>>(null);
  const [value, setValue] = useState(title);

  // Update local state when title prop changes
  useEffect(() => {
    setValue(title);
  }, [title]);

  useEffect(() => {
    if (!inputRef.current) return;
    inputRef.current.focus();
  }, []);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        onComplete();
      }
    },
    [onComplete]
  );

  const onInput = useCallback(
    (value: string) => {
      setValue(value);
      if (value) {
        onChange(value);
      }
    },
    [onChange]
  );

  return (
    <div>
      <div className="w-screen md:w-auto mt-3 mx-0 overflow-x-hidden min-h-[40px] pt-2 px-3 mb-2 bg-input text-foreground rounded-lg">
        <TextareaAutosize
          autoFocus={autofocus}
          ref={inputRef}
          spellCheck="false"
          rows={!value ? 1 : Math.ceil(value.length / 50)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          value={value === 'Untitled Collection' ? '' : value}
          onChange={e => {
            onInput(e.target.value);
          }}
          className={cn(
            'resize-none break-words bg-transparent text-lg outline-none w-full',
            bold && 'font-bold'
          )}
        />
      </div>
    </div>
  );
};

export default memo(CollectionTitle);
